import { escapeHtml } from "@/utils/sanitizeHtml";
import { splitSentences, contentToTopics } from "./summaryTopics";

/**
 * Resumo da consulta em duas versões — salvia-841 (Vidal, T41/IT-15).
 *
 * "Completa" é o resumo estruturado que a IA devolve (9 seções numeradas,
 * `1. Título\nconteúdo`). Na salvia-841 a "Resumida" era derivada daqui, de
 * forma determinística, para valer na hora em toda consulta já gravada.
 *
 * salvia-937 (T54/AJ-01, nutricionistas testadoras): a resumida deixou de
 * ser frase cortada com reticências. Agora ela é GERADA em tópicos pelo
 * nutria-comunicacao (agente resumo_topicos) e gravada em
 * `appointments.short_summary`. O que fica aqui é o fallback para consultas
 * sem essa coluna — também em tópicos, sem truncar (utils/summaryTopics).
 */

const EMPTY_SECTION =
  /^n[aã]o\s+(foi\s+)?(mencionad|informad|relatad|abordad|discutid)/i;

/** `1. Título\nconteúdo` → [{ title, content }]. */
export function parseSummarySections(text) {
  return String(text ?? "")
    .split(/(?=^\s*\d+\.\s)/m)
    .map((block) => block.trim())
    .filter((block) => /^\d+\.\s/.test(block))
    .map((block) => {
      const [firstLine, ...rest] = block.split("\n");
      return {
        title: firstLine.replace(/^\d+\.\s*/, "").trim(),
        content: rest.join("\n").trim(),
      };
    });
}

export function isEmptySection(content) {
  const text = String(content ?? "").trim();
  return !text || EMPTY_SECTION.test(text);
}

/** [{ title, content }] → `1. Título\nconteúdo` (renumerado). */
export function toNumberedText(sections) {
  return sections
    .map((s, i) =>
      s.content ? `${i + 1}. ${s.title}\n${s.content}` : `${i + 1}. ${s.title}`
    )
    .join("\n\n");
}

/**
 * salvia-937: `splitSentences` mora em utils/summaryTopics (sem dependência de
 * DOM, para o teste em node) e é reexportada daqui porque `wrapSentences`
 * abaixo a usa.
 */
export { splitSentences };

/**
 * Texto numerado da completa → texto numerado da resumida (FALLBACK).
 *
 * salvia-937: a resumida de verdade vem gravada em `short_summary`, gerada em
 * tópicos pela IA. Isto é o que a tela usa quando essa coluna está vazia
 * (consulta antiga, agente falhou, completa editada e ainda não regerada):
 * seções vazias somem e cada frase vira um tópico INTEIRO — nunca mais frase
 * cortada com reticências nem "(+N na versão completa)".
 *
 * Sem nada relevante, devolve "" — a tela diz isso com as próprias palavras.
 */
export function shortenNumberedText(text) {
  const sections = parseSummarySections(text)
    .filter((s) => !isEmptySection(s.content))
    .map((s) => ({ title: s.title, content: contentToTopics(s.content) }))
    .filter((s) => s.content);

  return toNumberedText(sections);
}

/**
 * A resumida que a tela mostra: a gravada (`short_summary`), se tiver seções
 * numeradas com conteúdo; senão o fallback acima. O texto gravado passa pelo
 * mesmo caminho do completo — `parseSummarySections`, e o HTML só nasce em
 * `formatSummaryHtml`, com escape.
 */
export function shortSummaryToDisplay(shortSummary, fullSummary) {
  const stored = String(shortSummary ?? "").trim();
  if (stored) {
    const sections = parseSummarySections(stored).filter((s) => !isEmptySection(s.content));
    if (sections.length) return toNumberedText(sections);
  }
  return shortenNumberedText(fullSummary);
}

/**
 * HTML gravado no banco (h4.summary-heading + p) → texto numerado.
 *
 * DOMParser em vez de `div.innerHTML`: documento inerte, então um
 * `<img onerror>` de registro legado não dispara durante a conversão.
 */
export function htmlToNumberedText(html) {
  const body = new DOMParser().parseFromString(
    String(html ?? ""),
    "text/html"
  ).body;
  const blocks = body.querySelectorAll("h4, p");
  if (!blocks.length) return (body.textContent || "").trim();

  let output = "";
  let n = 1;
  blocks.forEach((el) => {
    const nodeText = (el.textContent || "").trim();
    if (!nodeText) return;
    if (el.tagName.toLowerCase() === "h4") {
      output += `${n}. ${nodeText}\n`;
      n += 1;
    } else {
      output += `${nodeText}\n\n`;
    }
  });
  return output.trim();
}

/**
 * SALVIA-881: cada frase (ou item de lista) vira um `<span>` próprio.
 *
 * - `summary-sentence` sempre: é o alvo do "Ver no resumo" das notas, que
 *   acha e destaca a frase correspondente.
 * - `summary-link` só com transcrição: aí a frase também é clicável e leva ao
 *   trecho da conversa.
 *
 * Só o `<span>` é acrescentado: o texto continua o mesmo, então copiar e
 * baixar não mudam.
 */
function wrapSentences(content, linkable) {
  const openTag = linkable
    ? '<span class="summary-sentence summary-link" role="button" tabindex="0" title="Ver na transcrição">'
    : '<span class="summary-sentence">';
  return content
    .split("\n")
    .map((line) =>
      splitSentences(line)
        .map((sentence) => `${openTag}${escapeHtml(sentence)}</span>`)
        .join(" ")
    )
    .join("\n");
}

/**
 * Texto numerado → HTML da tela (classes `summary-*`).
 *
 * O escape vem ANTES da marcação: o texto é conteúdo gerado por IA externa, e
 * dado nunca vira tag.
 *
 * `sentences`: cada frase num `<span>` (alvo das notas). `linkable`: além
 * disso, splitSentences clicáveis para a transcrição. Seção vazia ("Não foi
 * mencionado…") nunca é quebrada — não há o que localizar nela.
 */
export function formatSummaryHtml(
  text,
  { linkable = false, sentences = false } = {}
) {
  return parseSummarySections(text)
    .map(({ title, content }) => {
      let html = `<h4 class="summary-heading">${escapeHtml(title)}</h4>`;
      if (content) {
        const isEmpty = isEmptySection(content);
        const cls = isEmpty
          ? "summary-paragraph summary-paragraph--muted"
          : "summary-paragraph";
        const body =
          (linkable || sentences) && !isEmpty
            ? wrapSentences(content, linkable)
            : escapeHtml(content);
        html += `<p class="${cls}">${body}</p>`;
      }
      return html;
    })
    .join("");
}
